/**
 * The "are you sure?" confirmation (the SweetAlert2 popup of the former
 * client): an icon, an optional title, a text, OK and Cancel.
 */
import { useEffect, useRef } from 'react';
import { dialogCancelHandler } from './dialog-cancel';

export type ConfirmDialogType = 'warning' | 'question';

export interface ConfirmDialogProps {
  text: string;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  /** Icon: an orange "!" for a warning (default), a blue "?" for a question. */
  type?: ConfirmDialogType;
  confirmLabel?: string;
  cancelLabel?: string;
}

const ICON_GLYPH: Record<ConfirmDialogType, string> = {
  warning: '!',
  question: '?',
};

export function ConfirmDialog({
  text,
  onConfirm,
  onCancel,
  title,
  type = 'warning',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
}: Readonly<ConfirmDialogProps>) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // Rendered means open; removing the element from the document closes it
    // without a `close` event (closing it here would fire one and call onClose).
    if (!dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      role="alertdialog"
      aria-label={title ?? 'Confirmation'}
      onCancel={dialogCancelHandler(onCancel)}
      className="app-dialog confirm-dialog"
      data-testid="confirm-dialog"
    >
      <div className="confirm-body">
        <div className={`confirm-icon confirm-icon-${type}`} aria-hidden="true">
          {ICON_GLYPH[type]}
        </div>
        {title && (
          <h2 className="confirm-title" data-testid="confirm-title">
            {title}
          </h2>
        )}
        <p className="confirm-text" data-testid="confirm-text">
          {text}
        </p>
        <div className="confirm-actions">
          <button
            type="button"
            className="confirm-button confirm-confirm"
            onClick={onConfirm}
            data-testid="confirm-ok-button"
          >
            {confirmLabel}
          </button>
          <button
            type="button"
            className="confirm-button confirm-cancel"
            onClick={onCancel}
            data-testid="confirm-cancel-button"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
